import React from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { Card, CardBody, CardTitle, Container, Row } from "reactstrap";

import Reveal from "motion/Reveal";
import { SERVICES } from "views/Services/services-config.js";
import ServiceIcon from "views/Services/ServiceIcon.js";
import ContactForm from "components/Forms/ContactForm";

import chrisSmallJPG from "assets/img/faces/chris_small.jpg";
import oliveJPG from "assets/img/faces/olive2.jpg";

// The three sections below the hero, lifted out of landing.js when the motion
// lab needed to render them repeatedly at different settings. Splitting them was
// the only way to show the same content under several treatments without the lab
// owning a second copy of it that would drift from the real page.
//
// <Reveal> carries the grid column's own classes rather than wrapping a <Col>.
// reactstrap 8's Col has no forwardRef so it cannot take the ref the observer
// needs, and an extra div between the column and `.service-card-link` would
// break the `height: 100%` chain that gives the four cards equal heights above
// 768px. Revealing the column also keeps the transform off the card itself,
// which already owns one: `.info.service-card:hover` lifts it 5px and `.card`
// lifts the team cards 10px on a bouncy curve that a reveal would inherit.

export function ServicesSection() {
  const { t } = useTranslation();

  return (
    <div className="section text-center">
      <Container>
        <Row>
          <Reveal className="ml-auto mr-auto col-md-8">
            <span className="section-rule" aria-hidden="true" />
            <h2 className="title">{t("landing.servicesTitle")}</h2>
            <p className="section-lede">{t("landing.servicesDescription")}</p>
            <br />
          </Reveal>
        </Row>
        <br />
        <br />
        <Row>
          {SERVICES.map((s, i) => (
            // `index` is a position in the row, not a duration. The step between
            // them lives in _motion.scss and applies only from md up, where the
            // cards are actually side by side — stacked, each arrives on its own
            // and a delay keyed to grid position would strand the third card on
            // screen waiting for a turn that no longer means anything.
            <Reveal className="col-md-3" index={i} key={s.key}>
              <Link to={`/${s.slug}`} className="service-card-link">
                <div className="info mgb-50 service-card">
                  <div className="service-card-image">
                    <img
                      src={s.image}
                      srcSet={s.imageSrcSet}
                      sizes="(max-width: 575.98px) calc(100vw - 30px), (max-width: 767.98px) 510px, (max-width: 991.98px) 150px, (max-width: 1199.98px) 210px, 255px"
                      alt=""
                      loading="lazy"
                      style={
                        s.imagePosition
                          ? { objectPosition: s.imagePosition }
                          : undefined
                      }
                    />
                  </div>
                  <div className="icon">
                    <ServiceIcon name={s.icon} size={21} draw />
                  </div>
                  <div className="description">
                    <h4 className="info-title mgb-20 m-mgt-0">
                      {t(`landing.service.${s.key}.title`)}
                    </h4>
                    <p>{t(`landing.service.${s.key}.body`)}</p>
                    <span className="btn btn-link btn-info learn-more-link">
                      {t("landing.service.learnMore")}
                    </span>
                  </div>
                </div>
              </Link>
            </Reveal>
          ))}
        </Row>
      </Container>
    </div>
  );
}

export function TeamSection() {
  const { t } = useTranslation();

  return (
    <div className="section section--tint text-center">
      <Container>
        <Reveal>
          <span className="section-rule" aria-hidden="true" />
          <h2 className="title">{t("team.title")}</h2>
        </Reveal>
        <Row>
          <Reveal className="col-md-6" index={0}>
            <Card className="card-profile card-plain">
              <div className="card-avatar">
                <a href="#pablo" onClick={(e) => e.preventDefault()}>
                  <img alt="olive" src={oliveJPG} />
                </a>
              </div>
              <CardBody>
                <a href="#pablo" onClick={(e) => e.preventDefault()}>
                  <div className="author">
                    <CardTitle tag="h4">{t("team.olive.name")}</CardTitle>
                    <h6 className="card-category">{t("team.olive.role")}</h6>
                  </div>
                </a>
                <p className="card-description text-center">
                  {t("team.olive.bio")}
                </p>
              </CardBody>
            </Card>
          </Reveal>
          <Reveal className="col-md-6" index={1}>
            <Card className="card-profile card-plain">
              <div className="card-avatar">
                <a href="#pablo" onClick={(e) => e.preventDefault()}>
                  <img alt="chris" src={chrisSmallJPG} />
                </a>
              </div>
              <CardBody>
                <a href="#pablo" onClick={(e) => e.preventDefault()}>
                  <div className="author">
                    <CardTitle tag="h4">{t("team.chris.name")}</CardTitle>
                    <h6 className="card-category">{t("team.chris.role")}</h6>
                  </div>
                </a>
                <p className="card-description text-center">
                  {t("team.chris.bio")}
                </p>
              </CardBody>
            </Card>
          </Reveal>
        </Row>
      </Container>
    </div>
  );
}

// `id` is a prop only so the lab can render a second, inert copy of this block
// without minting a duplicate #contact. Note that the form inside carries its own
// hard-coded id="contactForm" — which the hero's CTA links to — so the lab must
// still render the real form exactly once. See MotionLab.js.
export function ContactSection({ id = "contact" }) {
  const { t } = useTranslation();

  return (
    <div id={id} className="section landing-section">
      <Container>
        <Row>
          <Reveal className="ml-auto mr-auto col-md-8">
            <span className="section-rule" aria-hidden="true" />
            <h2 className="title">{t("contact.title")}</h2>
            <p className="mgt-20">{t("contact.description")}</p>
            <ContactForm />
          </Reveal>
        </Row>
      </Container>
    </div>
  );
}
